import { ICruiseSearchFacets } from './../../../interfaces/cruise/ICruiseSearchFacets';
import {
    IFrontPageConfig,
    IGlobalConfigData,
    ITranslations,
    IHorizontalContent,
    IUGC,
} from '@interfaces';
import { IShip, IShipSearchSource } from '@interfaces/ship';
import { ICruiseSearch } from '../../../interfaces/cruise';

import { fetchResource } from '../api';

const SORT_TYPES = {
    date: 'date',
    dateDesc: 'date_dest',
    rating: 'rating',
    priceAsc: 'price_asc',
    ratingDesc: 'rating_desc',
    quote: 'quote',
    discount: 'discount',
    relevance: 'relevance',
} as const;

const DEVICE_TYPE = {
    mobile: 'mobile',
    desktop: 'desktop',
} as const;

type SortByTypes = (typeof SORT_TYPES)[keyof typeof SORT_TYPES];
type DeviceTypes = (typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE];

interface APISearchProps {
    limit?: number;
    offset?: number;
    sortBy?: SortByTypes;
}

export const fetchConfig = () => {
    const url = `v2/base/config`;
    return fetchResource<IGlobalConfigData>(url);
};

export const fetchBaseData = () => {
    const url = 'v2/base';
    return fetchResource<IFrontPageConfig>(url);
};

export const fetchTranslations = () => {
    const url = 'v2/translations';
    return fetchResource<ITranslations>(url);
};

export const fetchShip = (slug: string) => {
    const url = `v2/ships/${slug}?device=desktop`;
    return fetchResource<IShip>(url);
};

export const fetchHorizontalContent = (slug: string) => {
    const url = `v2/content/horizontal?slug=${slug}`;
    return fetchResource<IHorizontalContent>(url);
};

interface ISearchShipProps extends APISearchProps {
    slug: string;
    device?: DeviceTypes;
}

export const searchShip = (props: ISearchShipProps) => {
    const {
        slug,
        limit = 10,
        offset = 0,
        device = DEVICE_TYPE.desktop,
        sortBy = SORT_TYPES.quote,
    } = props;
    const url = `v2/search/ships/${slug}?limit=${limit}&offset=${offset}&sortBy=${sortBy}&device=${device}`;
    return fetchResource<IShipSearchSource>(url);
};

interface ISearchCruisesProps extends APISearchProps {
    slug: string;
    device?: DeviceTypes;
}

export const searchCruises = (props: ISearchCruisesProps) => {
    const { slug, limit = 10, device = DEVICE_TYPE.desktop, sortBy = SORT_TYPES.quote } = props;
    const url = `v2/search/ships/${slug}/cruises/?limit=${limit}&sortBy=${sortBy}&device=${device}`;
    return fetchResource<ICruiseSearch>(url);
};

export interface ISearchCruiseFacetsProps extends APISearchProps {
    shipId: number | string;
    startDate?: number;
    endDate?: number;
    zoneId?: string | number;
}

export const searchCruiseFacets = (props: ISearchCruiseFacetsProps) => {
    const { shipId, startDate, endDate, zoneId, limit = 10 } = props;
    const queryParams: Record<string, string> = { limit: limit.toString() };

    if (zoneId) queryParams.zoneId = zoneId.toString();
    if (startDate) queryParams.startDate = startDate.toString();
    if (endDate) queryParams.endDate = endDate.toString();

    const queryString = new URLSearchParams(queryParams).toString();
    const url = `v2/search/ships/${shipId}/cruises/facets?${queryString}`;

    return fetchResource<ICruiseSearchFacets>(url);
};

interface IFetchUGCProps extends APISearchProps {
    slug: string;
}

export const fetchUGC = (props: IFetchUGCProps) => {
    const { slug, limit = 10, offset = 0, sortBy = SORT_TYPES.date } = props;
    const url = `v2/ugc/ships/${slug}/ratings/composite?limit=${limit}&offset=${offset}&sortBy=${sortBy}`;
    return fetchResource<IUGC>(url);
};

export interface ISubmitContactFormProps {
    channel: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    emailAddress: string;
    domain: string;
    userId: null | string;
    sessionId: null | string;
    message: string;
    bookingCode: string;
    contactMethod: string;
    bookingUrl: string;
}

export const submitShipContactForm = (
    props: ISubmitContactFormProps,
) => {
    const url = `advisor`;
    return fetchResource<{ isSuccessful?: boolean }>(url, {
        method: 'POST',
        body: JSON.stringify(props),
    });
};
